/**
 * Base Product Customizer Class - Optimized Version
 * Contains common functionality for both Bundle and Single product customizers
 * Following Horizon theme philosophy: minimalism and performance-first
 */

// Optimized throttle utility with better performance
function throttle(callback, delay = 250) {
  let timeoutId;
  let lastExecTime = 0;

  return function (...args) {
    const currentTime = Date.now();
    const timeSinceLastExec = currentTime - lastExecTime;

    if (timeSinceLastExec >= delay) {
      lastExecTime = currentTime;
      callback.apply(this, args);
    } else {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        lastExecTime = Date.now();
        callback.apply(this, args);
      }, delay - timeSinceLastExec);
    }
  };
}

// Cache for frequent DOM queries
const DOMCache = new Map();
const getCachedElement = (selector, context = document) => {
  const cacheKey = `${selector}_${context === document ? 'doc' : 'ctx'}`;
  if (!DOMCache.has(cacheKey)) {
    DOMCache.set(cacheKey, context.querySelector(selector));
  }
  return DOMCache.get(cacheKey);
};

// Base Customizer Class - contains all common functionality
class BaseCustomizer {
  constructor(config = {}) {
    // Core configuration with defaults
    this.config = {
      selectors: {
        customizer: '[data-customizer]',
        form: 'form[action="/cart/add"]',
        addToCartButton: '[name="add"]',
        variantInput: '[name="id"]',
        quantityInput: '[name="quantity"]',
        priceContainer: '.price',
        ...config.selectors
      },
      classes: {
        expanded: 'expanded',
        active: 'active',
        selected: 'selected',
        loading: 'loading',
        disabled: 'disabled',
        ...config.classes
      },
      // Performance settings
      throttleDelay: config.throttleDelay || 100,
      saveStateDelay: config.saveStateDelay || 300,
      ...config
    };

    // Core properties
    this.customizer = null;
    this.productForm = null;
    this.addToCartButton = null;
    this.variantIdInput = null;
    this.quantityInput = null;
    this.priceContainer = null;

    // State management
    this.isProcessing = false;
    this.currentOpenDropdown = null;
    this.shopCurrency = window.Shopify?.currency?.active || 'USD';
    this.isDesignMode = window.Shopify?.designMode || false;

    // Event handler bindings (prevent memory leaks)
    this.boundHandlers = new Map();

    // Create optimized throttled functions
    this.throttledUpdatePrice = throttle(() => this.updatePrice(), this.config.throttleDelay);

    // Initialize if auto-init is enabled (default true)
    if (config.autoInit !== false) {
      this.initialize();
    }
  }

  /**
   * Initialize the customizer with error handling
   */
  initialize() {
    try {
      if (!this.findElements()) {
        console.warn('BaseCustomizer: Required elements not found');
        return false;
      }

      this.bindEvents();
      this.initializeState();

      // Store instance reference with cleanup
      if (this.customizer) {
        if (this.customizer._customizerInstance) {
          this.customizer._customizerInstance.destroy();
        }
        this.customizer._customizerInstance = this;
      }

      return true;
    } catch (error) {
      console.error('BaseCustomizer: Initialization failed:', error);
      return false;
    }
  }

  /**
   * Optimized element finding with caching
   */
  findElements() {
    // Find main customizer element
    this.customizer = getCachedElement(this.config.selectors.customizer);
    if (!this.customizer) return false;

    // Find form elements with context optimization
    this.productForm = getCachedElement(this.config.selectors.form);
    if (!this.productForm) return false;

    this.addToCartButton = getCachedElement(this.config.selectors.addToCartButton, this.productForm);
    this.variantIdInput = getCachedElement(this.config.selectors.variantInput, this.productForm);
    this.quantityInput = getCachedElement(this.config.selectors.quantityInput, this.productForm);

    // Find price container
    this.priceContainer = getCachedElement(this.config.selectors.priceContainer);

    return !!(this.addToCartButton && this.variantIdInput);
  }

  /**
   * Optimized event binding with passive listeners
   */
  bindEvents() {
    // Create bound handlers once and store them
    this.boundHandlers.set('clickHandler', this.handleClick.bind(this));
    this.boundHandlers.set('variantChange', this.handleVariantChange.bind(this));
    this.boundHandlers.set('formSubmit', this.handleFormSubmit.bind(this));
    this.boundHandlers.set('addToCart', this.handleAddToCart.bind(this));

    // Bind single click handler with event delegation
    this.customizer.addEventListener('click', this.boundHandlers.get('clickHandler'), { passive: false });
    this.customizer.addEventListener('change', this.boundHandlers.get('variantChange'), { passive: true });

    // Bind form events
    if (this.productForm) {
      this.productForm.addEventListener('submit', this.boundHandlers.get('formSubmit'));
    }

    if (this.addToCartButton) {
      this.addToCartButton.addEventListener('click', this.boundHandlers.get('addToCart'));
    }

    // Bind Shopify theme editor events
    this.bindShopifyEvents();
  }

  /**
   * Bind Shopify theme editor events with debouncing
   */
  bindShopifyEvents() {
    const debouncedReinit = throttle(() => this.reinitialize(), 500);

    document.addEventListener('shopify:section:load', debouncedReinit);
    document.addEventListener('shopify:section:select', debouncedReinit);
    document.addEventListener('shopify:block:select', debouncedReinit);
  }

  /**
   * Main click handler with proper event delegation
   */
  handleClick(event) {
    // Handle dropdown toggle clicks first (highest priority)
    const dropdownToggle = event.target.closest('[data-dropdown-toggle]');
    if (dropdownToggle) {
      this.handleDropdownClick(event, dropdownToggle);
      return;
    }

    // Handle option clicks
    this.handleOptionClick(event);
  }

  /**
   * Handle dropdown toggle clicks
   */
  handleDropdownClick(event, dropdownToggle) {
    event.preventDefault();
    event.stopPropagation();

    const dropdownSection = dropdownToggle.closest('[data-variant-dropdown]');
    if (!dropdownSection) return;

    const dropdownContent = dropdownSection.querySelector('[data-dropdown-content]');
    if (!dropdownContent) return;

    this.toggleDropdown(dropdownSection, dropdownContent, dropdownToggle);
  }

  /**
   * Toggle dropdown state
   */
  toggleDropdown(section, content, toggle) {
    const isCurrentlyOpen = content.classList.contains(this.config.classes.expanded);

    // Close all other dropdowns
    this.closeAllDropdowns();

    // Toggle current dropdown
    if (!isCurrentlyOpen) {
      toggle.classList.add(this.config.classes.active);
      content.classList.add(this.config.classes.expanded);
      this.currentOpenDropdown = section;
    } else {
      this.currentOpenDropdown = null;
    }
  }

  /**
   * Close all open dropdowns
   */
  closeAllDropdowns() {
    const dropdowns = this.customizer.querySelectorAll('[data-variant-dropdown]');
    dropdowns.forEach(dropdown => {
      const header = dropdown.querySelector('[data-dropdown-toggle]');
      const content = dropdown.querySelector('[data-dropdown-content]');
      if (header) header.classList.remove(this.config.classes.active);
      if (content) content.classList.remove(this.config.classes.expanded);
    });
    this.currentOpenDropdown = null;
  }

  /**
   * Handle variant radio changes
   */
  handleVariantChange(event) {
    const radio = event.target;
    if (radio.type !== 'radio') return;

    if (
      radio.classList.contains('variant-radio') ||
      radio.classList.contains('extra-variant-radio') ||
      radio.classList.contains('direction-variant-radio')
    ) {
      this.handleVariantRadioChange(radio, event);
    }
  }

  /**
   * Handle option clicks (labels, containers)
   */
  handleOptionClick(event) {
    // Handle clicks on option containers
    const moduleOption = event.target.closest('.module-option');
    if (moduleOption && !event.target.matches('input[type="radio"]')) {
      const radio = moduleOption.querySelector('.variant-radio, .extra-variant-radio, .direction-variant-radio');
      if (radio && !radio.checked) {
        // Handle extra/direction variant exclusivity (radios sharing a name across
        // cloned quantity-line copies need manual unchecking of siblings)
        if (radio.classList.contains('extra-variant-radio') || radio.classList.contains('direction-variant-radio')) {
          const radioName = radio.getAttribute('name');
          const allRadiosInGroup = document.querySelectorAll(`input[name="${radioName}"]`);
          allRadiosInGroup.forEach(r => {
            if (r !== radio) r.checked = false;
          });
        }

        radio.checked = true;
        radio.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }
  }

  /**
   * Optimized variant radio change handling
   */
  handleVariantRadioChange(radio, event) {
    // Use requestAnimationFrame for non-critical DOM updates
    requestAnimationFrame(() => {
      this.updateVisualSelection(radio);
      this.updateDropdownHeader(radio);
    });

    // Handle dropdown progression immediately
    this.handleDropdownProgression(radio);

    // Update price displays (throttled)
    this.throttledUpdatePrice();

    // Trigger custom event for extensibility
    this.dispatchCustomEvent('variant:changed', {
      radio: radio,
      type: radio.classList.contains('extra-variant-radio') ? 'extra' : 'variant'
    });
  }

  /**
   * Update visual selection state
   */
  updateVisualSelection(radio) {
    const optionDiv = radio.closest('.module-option');
    if (!optionDiv) return;

    const parentGrid = optionDiv.parentElement;
    const radioName = radio.getAttribute('name');

    // Remove selected class from appropriate siblings
    if (radio.classList.contains('extra-variant-radio') || radio.classList.contains('direction-variant-radio')) {
      // For extra/direction variants, remove from all siblings
      const siblings = parentGrid.querySelectorAll('.module-option');
      siblings.forEach(s => s.classList.remove(this.config.classes.selected));
    } else {
      // For regular variants, only remove from same option group
      const siblings = parentGrid.querySelectorAll('.module-option');
      siblings.forEach(s => {
        const siblingRadio = s.querySelector('.variant-radio');
        if (siblingRadio && siblingRadio.getAttribute('name') === radioName) {
          s.classList.remove(this.config.classes.selected);
        }
      });
    }

    // Add selected class to current option
    optionDiv.classList.add(this.config.classes.selected);
  }

  /**
   * Update dropdown header with selected value
   */
  updateDropdownHeader(radio) {
    const dropdownSection = radio.closest('[data-variant-dropdown]');
    if (!dropdownSection) return;

    const selectedValueSpan = dropdownSection.querySelector('[data-selected-value]');
    if (!selectedValueSpan) return;

    // Update header text
    if (radio.classList.contains('variant-radio')) {
      selectedValueSpan.textContent = radio.dataset.optionValue || 'Select';
    } else if (radio.classList.contains('extra-variant-radio') || radio.classList.contains('direction-variant-radio')) {
      selectedValueSpan.textContent = radio.dataset.label || 'Select';
    }

    // Update price difference if applicable
    const priceDiffSpan = dropdownSection.querySelector('[data-price-tag]');
    if (priceDiffSpan && radio.classList.contains('variant-radio')) {
      this.updatePriceDifference(radio, priceDiffSpan);
    }
  }

  /**
   * Handle dropdown progression (close current, open next)
   */
  handleDropdownProgression(radio) {
    const dropdownSection = radio.closest('[data-variant-dropdown]');
    if (!dropdownSection) return;

    const dropdownHeader = dropdownSection.querySelector('[data-dropdown-toggle]');
    const dropdownContent = dropdownSection.querySelector('[data-dropdown-content]');

    if (dropdownHeader && dropdownContent) {
      dropdownHeader.classList.remove(this.config.classes.active);
      dropdownContent.classList.remove(this.config.classes.expanded);

      // Open next dropdown if available
      const allDropdowns = this.customizer.querySelectorAll('[data-variant-dropdown]');
      const currentIndex = Array.from(allDropdowns).indexOf(dropdownSection);

      if (currentIndex < allDropdowns.length - 1) {
        const nextDropdown = allDropdowns[currentIndex + 1];
        const nextHeader = nextDropdown.querySelector('[data-dropdown-toggle]');
        const nextContent = nextDropdown.querySelector('[data-dropdown-content]');

        if (nextHeader && nextContent) {
          nextHeader.classList.add(this.config.classes.active);
          nextContent.classList.add(this.config.classes.expanded);
          this.currentOpenDropdown = nextDropdown;
        }
      } else {
        this.currentOpenDropdown = null;
      }
    }
  }

  /**
   * Handle form submission
   */
  handleFormSubmit(event) {
    // Prevent default if already processing
    if (this.isProcessing) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    // Allow child classes to handle form submission
    const shouldContinue = this.onFormSubmit(event);
    if (!shouldContinue) {
      event.preventDefault();
      event.stopPropagation();
    }
  }

  /**
   * Handle add to cart button click
   */
  handleAddToCart(event) {
    // Prevent default if already processing
    if (this.isProcessing) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    // Allow child classes to handle add to cart
    const shouldContinue = this.onAddToCart(event);
    if (!shouldContinue) {
      event.preventDefault();
      event.stopPropagation();
    }
  }

  /**
   * Hook for child classes to handle form submission
   */
  onFormSubmit(event) {
    // To be overridden by child classes
    return true; // Allow form submission by default
  }

  /**
   * Hook for child classes to handle add to cart
   */
  onAddToCart(event) {
    // To be overridden by child classes
    return true; // Allow add to cart by default
  }

  /**
   * Initialize state (to be implemented by child classes)
   */
  initializeState() {
    // To be implemented by child classes
  }

  /**
   * Update price display (to be implemented by child classes)
   */
  updatePrice() {
    // To be implemented by child classes
  }

  /**
   * Save state (to be implemented by child classes)
   */
  saveState() {
    // To be implemented by child classes
  }

  /**
   * Common price calculation logic for bundle/single products
   */
  calculateBundlePrice(modules, options = {}) {
    const {
      includeComparePrice = true,
      useCustomizations = true
    } = options;

    let totalSellingPrice = 0;
    let totalComparePrice = 0;
    let hasSelectedModules = false;

    if (modules instanceof Map) {
      modules.forEach(module => {
        if (module.quantity > 0) {
          hasSelectedModules = true;
          const result = this.calculateModulePrice(module, { includeComparePrice, useCustomizations });
          totalSellingPrice += result.sellingPrice;
          totalComparePrice += result.comparePrice;
        }
      });
    }

    return {
      sellingPrice: totalSellingPrice,
      comparePrice: totalComparePrice,
      hasSelectedModules: hasSelectedModules
    };
  }

  /**
   * Calculate price for a single module
   */
  calculateModulePrice(module, options = {}) {
    const {
      includeComparePrice = true,
      useCustomizations = true
    } = options;

    let moduleSellingPrice = 0;
    let moduleComparePrice = 0;

    if (useCustomizations && module.customizations && module.customizations.length >= module.quantity) {
      // Use customizations for accurate pricing
      for (let i = 0; i < module.quantity; i++) {
        const customization = module.customizations[i];
        if (!customization || !customization.variantId) {
          // Fallback to module's current variant
          const price = module.currentVariant ? module.currentVariant.price : module.productPrice;
          const comparePrice = module.currentVariant ? (module.currentVariant.compare_at_price || price) : (module.productComparePrice || price);
          moduleSellingPrice += price;
          moduleComparePrice += comparePrice;
          continue;
        }

        const variant = module.variants?.find(v => v.id === customization.variantId);
        if (!variant) {
          const price = module.currentVariant ? module.currentVariant.price : module.productPrice;
          const comparePrice = module.currentVariant ? (module.currentVariant.compare_at_price || price) : (module.productComparePrice || price);
          moduleSellingPrice += price;
          moduleComparePrice += comparePrice;
          continue;
        }

        let itemPrice = variant.price;
        let itemComparePrice = variant.compare_at_price || variant.price;

        moduleSellingPrice += itemPrice;
        moduleComparePrice += itemComparePrice;
      }
    } else {
      // Fallback to simple calculation
      const price = module.currentVariant ? module.currentVariant.price : module.productPrice;
      const comparePrice = module.currentVariant ? module.currentVariant.compare_at_price : module.productComparePrice;

      moduleSellingPrice += module.quantity * price;

      if (includeComparePrice && comparePrice && comparePrice > price) {
        moduleComparePrice += module.quantity * comparePrice;
      } else {
        moduleComparePrice += module.quantity * price;
      }
    }

    return {
      sellingPrice: moduleSellingPrice,
      comparePrice: moduleComparePrice
    };
  }

  /**
   * Common state management - Save to storage
   */
  saveStateToStorage(storageKey, stateData) {
    try {
      const serializedData = JSON.stringify(stateData);
      this.setStorageItem(storageKey, serializedData);
      return true;
    } catch (error) {
      console.warn('Failed to save state to storage:', error);
      return false;
    }
  }

  /**
   * Common state management - Load from storage
   */
  loadStateFromStorage(storageKey) {
    try {
      const data = this.getStorageItem(storageKey);
      return data ? JSON.parse(data) : null;
    } catch (error) {
      console.warn('Failed to load state from storage:', error);
      return null;
    }
  }

  /**
   * Optimized cart item preparation with Map for better performance
   */
  prepareCartItems(modules, extraVariantsSelections, directionVariantsSelections) {
    const cartItems = [];

    if (!(modules instanceof Map)) return cartItems;

    for (const [moduleHandle, module] of modules) {
      if (module.quantity <= 0) continue;

      // Helper function to extract variant properties with option names
      const getVariantProperties = (variantId, extraVariants, directionVariants) => {
        const properties = {};

        // Find variant to get its title for option values
        if (module.variants && module.optionNames) {
          const variant = module.variants.find(v => v.id === variantId);
          if (variant && variant.title) {
            const optionValues = variant.title.split(' / ').map(v => v.trim());
            // Map option names to values
            module.optionNames.forEach((optionName, index) => {
              if (optionValues[index]) {
                properties[optionName] = optionValues[index];
              }
            });
          }
        }

        // Add extra variants (Kumaş seçimi)
        if (extraVariants && extraVariants.length > 0) {
          properties["Kumaş seçimi"] = extraVariants.map(ev => ev.label).join(', ');
        }

        // Add direction variants (Yön)
        if (directionVariants && directionVariants.length > 0) {
          properties["Yön"] = directionVariants.map(dv => dv.label).join(', ');
        }

        return properties;
      };

      // Handle multiple customizations with optimization
      if (module.customizations?.length > 0) {
        const uniqueCustomizations = new Map();

        // Process only needed customizations
        const relevantCustomizations = module.customizations.slice(0, module.quantity);

        for (const customization of relevantCustomizations) {
          const variantId = customization?.variantId || module.currentVariant?.id || module.productId;
          const extraVariants = customization?.extraVariants || [];
          const directionVariants = customization?.directionVariants || [];

          const key = `${variantId}_${extraVariants.length > 0 ? JSON.stringify(extraVariants) : ''}_${directionVariants.length > 0 ? JSON.stringify(directionVariants) : ''}`;

          if (uniqueCustomizations.has(key)) {
            uniqueCustomizations.get(key).quantity++;
          } else {
            uniqueCustomizations.set(key, {
              variantId,
              extraVariants,
              directionVariants,
              quantity: 1
            });
          }
        }

        // Add each unique customization
        for (const customization of uniqueCustomizations.values()) {
          const properties = getVariantProperties(customization.variantId, customization.extraVariants, customization.directionVariants);

          cartItems.push({
            id: customization.variantId,
            quantity: customization.quantity,
            properties
          });
        }
      } else {
        // Single customization - optimized
        const productId = module.currentVariant?.id || parseInt(module.productId);
        if (!productId) continue;

        const extraVariants = extraVariantsSelections?.get?.(moduleHandle);
        const directionVariants = directionVariantsSelections?.get?.(moduleHandle);
        const properties = getVariantProperties(productId, extraVariants, directionVariants);

        cartItems.push({
          id: productId,
          quantity: module.quantity,
          properties
        });
      }
    }

    return cartItems;
  }

  /**
   * Optimized money formatting with caching
   */
  formatMoney(cents) {
    // Early return for invalid values
    if (!cents && cents !== 0) return this.shopCurrency === 'TRY' ? '0 TL' : '$0.00';

    const numCents = typeof cents === 'number' ? cents : parseFloat(cents);
    if (isNaN(numCents)) return this.shopCurrency === 'TRY' ? '0 TL' : '$0.00';

    const amount = numCents / 100;

    // Cache formatters for better performance
    if (!this._formatters) {
      this._formatters = new Map();
    }

    const cacheKey = `${this.shopCurrency}_${amount % 1 === 0 ? 'int' : 'dec'}`;

    if (!this._formatters.has(cacheKey)) {
      if (this.shopCurrency === 'TRY') {
        this._formatters.set(cacheKey, new Intl.NumberFormat('tr-TR', {
          minimumFractionDigits: amount % 1 === 0 ? 0 : 2,
          maximumFractionDigits: 2
        }));
      } else {
        this._formatters.set(cacheKey, new Intl.NumberFormat(document.documentElement.lang || 'en', {
          style: 'currency',
          currency: this.shopCurrency,
          minimumFractionDigits: amount % 1 === 0 ? 0 : 2,
          maximumFractionDigits: 2
        }));
      }
    }

    const formatter = this._formatters.get(cacheKey);

    if (this.shopCurrency === 'TRY') {
      return formatter.format(amount) + ' TL';
    } else {
      return formatter.format(amount);
    }
  }

  /**
   * Renders the price display HTML based on selling and compare prices.
   * Uses Horizon theme's actual price structure to avoid conflicts.
   * @param {number} sellingPrice - The final selling price in cents.
   * @param {number} comparePrice - The compare at price in cents.
   * @returns {string} - The HTML string for the price display.
   */
  renderPriceDisplay(sellingPrice, comparePrice) {
    const showSalePrice = comparePrice && comparePrice > sellingPrice;
    const formattedSellingPrice = this.formatMoney(sellingPrice);
    const formattedComparePrice = showSalePrice ? this.formatMoney(comparePrice) : '';

    if (showSalePrice) {
      return `
        <div ref="priceContainer">
          <span role="group">
            <span class="visually-hidden">Sale price&nbsp;</span>
            <span class="price">${formattedSellingPrice}</span>
          </span>
          <span role="group">
            <span class="visually-hidden">Regular price&nbsp;</span>
            <span class="compare-at-price">${formattedComparePrice}</span>
          </span>
        </div>
      `;
    } else {
      return `
        <div ref="priceContainer">
          <span class="price">${formattedSellingPrice}</span>
        </div>
      `;
    }
  }

  /**
   * Renders just the inner content for price container (when targeting [ref="priceContainer"] directly)
   */
  renderPriceDisplayInner(sellingPrice, comparePrice) {
    const showSalePrice = comparePrice && comparePrice > sellingPrice;
    const formattedSellingPrice = this.formatMoney(sellingPrice);
    const formattedComparePrice = showSalePrice ? this.formatMoney(comparePrice) : '';

    if (showSalePrice) {
      return `
        <span role="group">
          <span class="visually-hidden">Sale price&nbsp;</span>
          <span class="price">${formattedSellingPrice}</span>
        </span>
        <span role="group">
          <span class="visually-hidden">Regular price&nbsp;</span>
          <span class="compare-at-price">${formattedComparePrice}</span>
        </span>
      `;
    } else {
      return `<span class="price">${formattedSellingPrice}</span>`;
    }
  }

  /**
   * Show loading state on button
   */
  showLoadingState(button = this.addToCartButton) {
    if (!button) return;

    button.setAttribute('aria-disabled', 'true');
    button.classList.add(this.config.classes.loading);

    const spinner = button.querySelector('.loading-overlay__spinner');
    if (spinner) {
      spinner.classList.remove('hidden');
    }
  }

  /**
   * Hide loading state on button
   */
  hideLoadingState(button = this.addToCartButton) {
    if (!button) return;

    button.setAttribute('aria-disabled', 'false');
    button.classList.remove(this.config.classes.loading);

    const spinner = button.querySelector('.loading-overlay__spinner');
    if (spinner) {
      spinner.classList.add('hidden');
    }
  }

  /**
   * Optimized toast message with single instance
   */
  showMessage(message, type = 'info') {
    if (!message) return;

    // Remove existing toast efficiently
    const existingToast = document.querySelector('.customizer-toast');
    if (existingToast) {
      existingToast.remove();
    }

    // Create optimized toast
    const toast = document.createElement('div');
    toast.className = `customizer-toast customizer-toast--${type}`;

    // Use CSS custom properties for consistent styling
    toast.style.cssText = `
      position: fixed;
      bottom: 20px;
      right: 20px;
      z-index: 10000;
      background-color: ${type === 'error' ? '#dc3545' : type === 'success' ? '#28a745' : '#17a2b8'};
      color: white;
      padding: 15px 20px;
      border-radius: 4px;
      box-shadow: 0 2px 10px rgba(0,0,0,0.1);
      font-size: 14px;
      max-width: 300px;
      word-break: break-word;
      animation: slideInUp 0.3s ease-out;
    `;

    toast.innerHTML = `
      <div style="display: flex; justify-content: space-between; align-items: center;">
        <div>${message}</div>
        <button style="background: none; border: none; font-size: 18px; cursor: pointer; margin-left: 10px; color: inherit;" 
                aria-label="Close">×</button>
      </div>
    `;

    // Bind close event
    toast.querySelector('button').addEventListener('click', () => toast.remove());

    document.body.appendChild(toast);

    // Auto-remove with cleanup
    setTimeout(() => toast.remove(), 5000);
  }

  /**
   * Dispatch custom events for extensibility
   */
  dispatchCustomEvent(eventName, detail = {}) {
    const event = new CustomEvent(`customizer:${eventName}`, {
      bubbles: true,
      detail: {
        customizer: this,
        ...detail
      }
    });

    this.customizer.dispatchEvent(event);
  }

  /**
   * Add to cart via AJAX, dispatching the theme's CartLinesUpdateEvent so
   * cart-icon, cart-drawer, and cart-items-component update the same way
   * they do for the theme's own product form (see product-form.js).
   */
  async addToCart(items, properties = {}) {
    const lineItems = Array.isArray(items) ? items : [items];
    let deferredEventPromise;

    try {
      this.isProcessing = true;
      this.showLoadingState();

      const { CartLinesUpdateEvent } = await import('@shopify/events');
      deferredEventPromise = CartLinesUpdateEvent.createPromise();

      document.dispatchEvent(
        new CartLinesUpdateEvent({
          action: 'add',
          context: 'product',
          lines: lineItems.map(item => ({
            merchandiseId: item.id,
            quantity: item.quantity
          })),
          promise: deferredEventPromise.promise
        })
      );

      const cartData = { items: lineItems };

      // Add global properties if provided
      if (Object.keys(properties).length > 0) {
        cartData.attributes = properties;
      }

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(cartData)
      });

      const itemCount = lineItems.reduce((sum, item) => sum + (item.quantity || 1), 0);

      if (!response.ok) {
        const errorData = await response.json();
        const refreshedCart = await this.refreshCartState();
        deferredEventPromise.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(refreshedCart),
          detail: {
            didError: true,
            items: refreshedCart.items,
            itemCount
          }
        });
        throw new Error(errorData.description || 'Error adding to cart');
      }

      const data = await response.json();
      const refreshedCart = await this.refreshCartState();

      deferredEventPromise.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(refreshedCart),
        detail: {
          didError: false,
          items: refreshedCart.items,
          itemCount
        }
      });

      return data;

    } catch (error) {
      console.error('Error adding to cart:', error);
      deferredEventPromise?.reject(error);
      this.showMessage(error.message, 'error');
      throw error;
    } finally {
      this.isProcessing = false;
      this.hideLoadingState();
    }
  }

  /**
   * Fetch the current cart state (used to resolve CartLinesUpdateEvent with
   * an accurate total quantity), mirroring product-form.js's #refreshCart().
   */
  async refreshCartState() {
    const cartItemsComponent = document.querySelector('cart-items-component');

    if (cartItemsComponent) {
      await customElements.whenDefined('cart-items-component');
      return cartItemsComponent.fetchCartData();
    }

    return fetch(`${(typeof Theme !== 'undefined' && Theme.routes?.cart_url) || '/cart'}.json`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin'
    }).then(response => response.json());
  }

  /**
   * Update form variant ID
   */
  updateFormVariantId(variantId) {
    if (!this.variantIdInput || !variantId) return;

    this.variantIdInput.value = variantId;

    // Trigger events for theme compatibility
    const inputEvent = new Event('input', { bubbles: true });
    this.variantIdInput.dispatchEvent(inputEvent);

    const changeEvent = new Event('change', { bubbles: true });
    this.variantIdInput.dispatchEvent(changeEvent);
  }

  /**
   * Storage helpers using localStorage as primary, sessionStorage as fallback
   */
  getStorageItem(key) {
    try {
      return localStorage.getItem(key) || sessionStorage.getItem(key);
    } catch (error) {
      console.warn('Error reading from storage:', error);
      return null;
    }
  }

  setStorageItem(key, value) {
    try {
      localStorage.setItem(key, value);
      sessionStorage.setItem(key, value); // Backup
    } catch (error) {
      console.warn('Error writing to storage:', error);
    }
  }

  removeStorageItem(key) {
    try {
      localStorage.removeItem(key);
      sessionStorage.removeItem(key);
    } catch (error) {
      console.warn('Error removing from storage:', error);
    }
  }

  /**
   * Reinitialize customizer (useful for theme editor)
   */
  reinitialize() {
    if (this.isDesignMode) {
      // Small delay for theme editor
      setTimeout(() => {
        this.cleanup();
        this.initialize();
      }, 100);
    }
  }

  /**
   * Optimized cleanup with complete memory leak prevention
   */
  cleanup() {
    // Remove all bound event listeners
    if (this.customizer && this.boundHandlers) {
      this.customizer.removeEventListener('click', this.boundHandlers.get('clickHandler'));
      this.customizer.removeEventListener('change', this.boundHandlers.get('variantChange'));
    }

    if (this.productForm && this.boundHandlers.has('formSubmit')) {
      this.productForm.removeEventListener('submit', this.boundHandlers.get('formSubmit'));
    }

    if (this.addToCartButton && this.boundHandlers.has('addToCart')) {
      this.addToCartButton.removeEventListener('click', this.boundHandlers.get('addToCart'));
    }

    // Clear bound handlers
    this.boundHandlers.clear();

    // Clear formatters cache
    if (this._formatters) {
      this._formatters.clear();
    }

    // Clear instance reference
    if (this.customizer?._customizerInstance) {
      this.customizer._customizerInstance = null;
    }

    // Reset state
    this.currentOpenDropdown = null;
    this.isProcessing = false;
  }

  /**
   * Optimized destroy with cache clearing
   */
  destroy() {
    this.cleanup();

    // Clear DOM cache entries related to this instance
    DOMCache.clear();

    // Clear all references
    this.customizer = null;
    this.productForm = null;
    this.addToCartButton = null;
    this.variantIdInput = null;
    this.quantityInput = null;
    this.priceContainer = null;
    this.config = null;
  }
}

// Add CSS animation for toast
if (!document.querySelector('#customizer-animations')) {
  const style = document.createElement('style');
  style.id = 'customizer-animations';
  style.textContent = `
    @keyframes slideInUp {
      from {
        opacity: 0;
        transform: translateY(20px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }
  `;
  document.head.appendChild(style);
}

// Export for module systems or global access
if (typeof module !== 'undefined' && module.exports) {
  module.exports = BaseCustomizer;
} else {
  window.BaseCustomizer = BaseCustomizer;
} 